import 'ol/ol.css'
import './style.css';

import Map from 'ol/Map';
import View from 'ol/View';

import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';

import OSM from 'ol/source/OSM';
import VectorSource from 'ol/source/Vector';

import GeoJSON from 'ol/format/GeoJSON';

import { Fill, Stroke, Style, Icon, Text, Circle } from 'ol/style';
import { OverviewMap, defaults as defaultControls } from 'ol/control';

import { transform } from 'ol/proj';

// -----------------------------
// DOM References
// -----------------------------
const mapbox = document.getElementById("mapbox");

// -----------------------------
// Constants & Configurations
// -----------------------------
const ICON_BY_ENTITY_TYPE = {
    QuickDrawBranch: "img/qdpin.png",
    AuthDealer: "img/dealerpin.png",
    AuthRepair: "img/repairpin.png",
};

const LABELS_BY_LANG = {
    0: {  // English
        phone: "Phone:",
        website: "Website:",
        maps: "Show on Google maps...",
        days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
    },
    1: {  // French
        phone: "Téléphone:",
        website: "Site web:",
        maps: "Afficher sur Google maps...",
        days: ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"]
    },
    2: {  // Spanish
        phone: "Teléfono:",
        website: "Sitio web:",
        maps: "Mostrar en Google maps...",
        days: ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"]
    },
};

// -----------------------------
// Helpers
// -----------------------------
function getPageLangIndex() {
    const page = window.location.pathname.split("/").pop();

    // Determines language index based on current page filename (0 = English, 1 = French, 2 = Spanish)
    if (page === "contact-fr.html") return 1;
    if (page === "contact-es.html") return 2;
    else return 0;  // Default: English
}

function getDayValue(dayField, langIndex) {
    // Get value by language index from dayField array (safe fallback if null)
    if (Array.isArray(dayField)) return dayField[langIndex] ?? dayField[0] ?? "";
    return dayField ?? "";
}

function safeWebsiteHref(website) {
    if (!website) return "";
    return website.startsWith("http") ? website : `http://${website}`;  // Ensure URL contains protocol
}

function getEntityIcon(feature) {
    // Resolve appropriate icon based on the feature's entity type
    const entityType = String(feature.get("EntityType") ?? "");  // Empty value if entityType is null
    return ICON_BY_ENTITY_TYPE[entityType] ?? "";
}

// -----------------------------
// Styles
// -----------------------------
function iconStyleFunction(feature) {
    const icon = getEntityIcon(feature);

    return [
        new Style({
            image: new Icon({
                src: icon,
                // rotation: properties['Heading'] * (Math.PI/180.0),
            }),
        }),
    ];
}

function iconStyleFunction2(feature) {
    const icon = getEntityIcon(feature);
    const label = String(feature.get("Label") ?? "");

    return [
        new Style({
            image: new Circle({
                radius: 18.0,
                fill: new Fill({
                    color: [247, 245, 179, 0.2]
                }),
                stroke: new Stroke({
                    color: [255, 102, 102, 1],
                    width: 4.0
                }),
            }),
        }),
        new Style({
            image: new Icon({
                src: icon,
                // rotation: properties['Heading'] * (Math.PI/180.0),
            }),
            text: new Text({
                text: label,
                offsetY: 0,
                offsetX: -20,
                font: 'bold 16px Calibri,sans-serif',
                fill: new Fill({
                    color: 'black',
                }),
                stroke: new Stroke({
                    color: 'white',
                    width: 4,
                }),
            }),
        }),
    ];
}

const iconStyle = new Style({
    image: new Icon({
        anchor: [0.5, 46],
        anchorXUnits: 'fraction',
        anchorYUnits: 'pixels',
        src: 'data/icon.png',
    }),
});

/*
const pointStyleHighlighted = new Style({  // Highlight selected location
    image: new Circle({
        radius: 4.0,
        fill: new Fill({
            color: [247, 245, 179,0.9]
        }),
        stroke: new Stroke({
            color: [65,65,110,1],
            width: 4.0
        }),
    }),
});
*/

// -----------------------------
// Mapbox Info Popup Helpers
// -----------------------------
function buildMapboxHtml(props, geometry, langIndex) {
    // Select language labels, fallback to English
    const labels = LABELS_BY_LANG[langIndex] ?? LABELS_BY_LANG[0];

    // Location information from properties (see locations.geojson)
    const name = props.Name ?? "";
    const address = props.Address ?? "";
    const city = props.City ?? "";
    const state = props.State ?? "";
    const zip = props.Zip ?? "";

    // Contact details
    const phone = props.Phone ?? "";
    const website = props.URL ?? "";

    // Hours of operation
    const mon = getDayValue(props.Mon, langIndex);
    const tue = getDayValue(props.Tue, langIndex);
    const wed = getDayValue(props.Wed, langIndex);
    const thu = getDayValue(props.Thu, langIndex);
    const fri = getDayValue(props.Fri, langIndex);
    const sat = getDayValue(props.Sat, langIndex);
    const sun = getDayValue(props.Sun, langIndex);

    // Convert map projection coordinates for external Google Maps link
    const [lon, lat] = transform(geometry.getCoordinates(), "EPSG:3857", "EPSG:4326");

    const href = safeWebsiteHref(website);  // Normalize website URL

    // Build and return HTML for the info popup
    return `
        <span style="font-weight:700;">${name}</span><br>
        ${address}<br>
        <span style="font-weight:700;">${city}</span>, ${state}, ${zip}<br>
    
        <table cellspacing="0" cellpadding="0" border="0">
            <tr>
                <td>${labels.phone}</td>
                <td><span style="font-weight:700;">${phone}</span></td>
            </tr>
            <tr>
                <td width="70px">${labels.website}</td>
                <td>
                    ${website 
                        ? `<a href="${href}" target="_blank" rel="noopener">${website}</a>` 
                        : ""
                    }
                </td>
            </tr>
        </table>
    
        <table cellspacing="0" cellpadding="0" border="0">
            <tr><td width="40px">${labels.days[0]}:</td><td>${mon}</td></tr>
            <tr><td>${labels.days[1]}:</td><td>${tue}</td></tr>
            <tr><td>${labels.days[2]}:</td><td>${wed}</td></tr>
            <tr><td>${labels.days[3]}:</td><td>${thu}</td></tr>
            <tr><td>${labels.days[4]}:</td><td>${fri}</td></tr>
            <tr><td>${labels.days[5]}:</td><td>${sat}</td></tr>
            <tr><td>${labels.days[6]}:</td><td>${sun}</td></tr>
        </table>
        
        <a href="https://maps.google.com/maps?q=loc:${lat.toFixed(8)},${lon.toFixed(8)}" target="_blank" rel="noopener"> 
            ${labels.maps}
        </a>
    `;
}

function showMapbox(feature) {
    // Populate and display the info popup for the selected feature
    if (!mapbox) return;

    const props = feature.getProperties();
    const geometry = feature.getGeometry();
    const langIndex = getPageLangIndex();

    mapbox.innerHTML = buildMapboxHtml(props, geometry, langIndex);
    mapbox.style.display = "block";
}

function hideMapbox() {
    // Clear and hide info popup
    if (!mapbox) return;
    mapbox.innerHTML = "";
    mapbox.style.display = "none";
}

// -----------------------------
// Zooming & Scrolling
// -----------------------------
function setupZoomHintAndScrollLock() {
    const mapEl = document.getElementById("map");
    const zoomHint = document.getElementById("map-zoom-hint");

    if (!mapEl || !zoomHint) return;

    // Show hint when hovering over map
    mapEl.addEventListener("mouseenter", () => {
        zoomHint.classList.add("visible");
    });

    // Hide hint when mouse leaves map
    mapEl.addEventListener("mouseleave", () => {
        zoomHint.classList.remove("visible");
    });

    // Hide hint if Ctrl/Cmd is held (zoom mode is active)
    document.addEventListener("keydown", (e) => {
        if ((e.ctrlKey || e.metaKey) && mapEl.matches(":hover")) {
            zoomHint.classList.remove("visible");
        }
    });

    document.addEventListener("keyup", (e) => {
        if (!(e.ctrlKey || e.metaKey) && mapEl.matches(":hover")) {
            zoomHint.classList.add("visible");
        }
    });

    // Ctrl/Cmd + scroll allows OpenLayers to zoom the map
    document.addEventListener(
        "wheel",
        (e) => {
            // Handle wheel events inside the map container
            const inMap = e.target?.closest?.("#map");
            if (!inMap) return;

            // If Ctrl/Cmd is held, allow OL to zoom normally
            if (e.ctrlKey || e.metaKey) return;

            // Otherwise, block OL from handling this wheel event (prevent map zoom while scrolling page)
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation?.();

            // Scroll the page instead
            window.scrollBy({
                top: e.deltaY,
                left: 0,
                behavior: "auto",
            });
        },
        // Allows preventDefault() to work, allow event cancel, handler runs before OL wheel handlers
        { passive: false, capture: true }
    );
}

// -----------------------------
// OL Setup & Config
// -----------------------------
const source = new OSM();

const overviewMapControl = new OverviewMap({
    layers: [
        new TileLayer({
            source: source,
        }),
    ],
});

const vectorSource = new VectorSource({
    url: "locations.geojson",
    format: new GeoJSON(),
});

const vectorLayer = new VectorLayer({
    source: vectorSource,
    style: iconStyleFunction,
    visible: true,
});

const map = new Map({
    target: 'map',
    layers: [
        new TileLayer({
            source: new OSM(),
        }),
        vectorLayer,
    ],
    view: new View({
        center: transform([-96.0, 43.0], 'EPSG:4326', 'EPSG:3857'),
        zoom: 4.8,
    }),
    controls: defaultControls({
        attributionOptions: ({
            collapsible: true
        })
    }).extend([overviewMapControl]),
});

// -----------------------------
// Selection State Tracking
// -----------------------------
let selectedFeature = null;

function setSelectedFeature(feature) {
    // Reset previous selection, apply highlight style to the new feature
    if (selectedFeature) selectedFeature.setStyle(iconStyleFunction);

    selectedFeature = feature;
    if (selectedFeature) selectedFeature.setStyle(iconStyleFunction2);
}

map.on("singleclick", (e) => {
    // Detect the first feature under the click location (hitTolerance radius specified)
    const feature = map.forEachFeatureAtPixel(
        e.pixel,
        (f) => f,
        { hitTolerance: 3 }
    );

    // Empty map area clicked, clear and hide info popup
    if (!feature) {
        setSelectedFeature(null);
        hideMapbox();
        return;
    }

    // Select the clicked feature, display corresponding details
    setSelectedFeature(feature);
    showMapbox(feature);
});

/*
vectorLayer.once('postrender', function() {
    for (var uid in vectorLayer.sourceChangeKey_.target.uidIndex_) {
        LocationList.push(vectorLayer.sourceChangeKey_.target.uidIndex_[uid])
    }
    console.log(vectorLayer);
});
*/

// -----------------------------
// Initialization
// -----------------------------
setupZoomHintAndScrollLock();
